import API from "../lib/TimeEditAPI";
import PropTypes from "prop-types";
import React from "react";
import _ from "underscore";
import Language from "../lib/Language";
import FieldTableRow from "./FieldTableRow";
import Mousetrap from "@timeedit/mousetrap";
import Log from "../lib/Log";
import OrganizationList from "./ObjectListWithDropdown";
import FieldInput from "./FieldInput";
import { Button } from "@timeedit/ui-components/components/Button/Button";
import { TFieldDefClass, TOrganizationId } from "../types/api/APIClassTypes";

/** A field of the edited reservations, with the values being edited. */
type TEditableField = {
    id: number;
    values: string[];
};

type TReservationFieldEditorProps = {
    reservationIds: number[];
    /** Field definitions, used to look up each edited field's definition. */
    fieldDefs: TFieldDefClass[];
    /**
     * The organisations the edited reservations already belong to. The save path
     * also tests this against false, which nothing passes.
     */
    organizations: TOrganizationId[] | false;
    /** True while every edited reservation has the same organisations. */
    organizationsEnabled: boolean;
    onClose: () => void;
    /** Called after a successful save; this component calls it without arguments. */
    onUpdate?: (ids?: number[]) => void;
    useNewUI?: boolean;
};

type TReservationFieldEditorState = {
    editableFields: TEditableField[];
    /** The ids of the fields that hold more than one value. */
    multipleValueFields: number[];
    /** The edited copies, or null while nothing has been changed. */
    updatedFields: TEditableField[] | null;
    organizations: TOrganizationId[];
    canEditOrganizations: boolean;
    selectedOrganizations: TOrganizationId[];
    fieldsValid: boolean;
};

class ReservationFieldEditor extends React.Component<
    TReservationFieldEditorProps,
    TReservationFieldEditorState
> {
    _isMounted = false;
    /** The "mod+s" binding this editor replaced, restored on unmount. */
    _previousSaveBinding: any = null;

    static propTypes = {
        reservationIds: PropTypes.array.isRequired,
        onClose: PropTypes.func.isRequired,
    };

    static defaultProps = {
        reservationIds: [],
    };

    constructor(props) {
        super(props);

        this.state = {
            editableFields: [],
            multipleValueFields: [],
            updatedFields: null,
            organizations: [],
            canEditOrganizations: this.props.organizationsEnabled || false,
            selectedOrganizations: this.props.organizations || [],
            fieldsValid: true,
        };
    }

    componentDidMount() {
        this._isMounted = true;
        this.bindSave();
        if (this.props && this.props.reservationIds) {
            this.loadInformation(
                this.props.reservationIds,
                this.state.selectedOrganizations,
                this.props.organizationsEnabled
            );
        }
    }

    componentDidUpdate(prevProps) {
        if (this.shouldLoadInformation(this.props, prevProps)) {
            this.loadInformation(
                this.props.reservationIds,
                this.state.selectedOrganizations,
                this.props.organizationsEnabled
            );
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        Mousetrap.unbindWithHelp("mod+s");

        if (this._previousSaveBinding) {
            Mousetrap.bindWithHelp(
                "mod+s",
                this._previousSaveBinding,
                undefined,
                Language.get("dialog_save")
            );
            this._previousSaveBinding = null;
        }
    }

    shouldLoadInformation(props, prevProps) {
        return !_.isEqual(props, prevProps);
    }

    loadInformation(reservationIds, selectedOrganizations, organizationsEnabled) {
        API.getModifiableReservationFields(reservationIds, (editableFields, hasSingleValueList) => {
            const multipleValueFields = _.pluck(
                editableFields.filter((field, index) => !hasSingleValueList[index]),
                "id"
            );

            API.okToSetReservationOrganizations(reservationIds, (response) => {
                API.getOrganizations({}, (result) => {
                    if (this._isMounted) {
                        this.setState({
                            selectedOrganizations: selectedOrganizations.map((org) =>
                                this.getDefinition(org.id, result.parameters[0])
                            ),
                            organizations: result.parameters[0],
                            canEditOrganizations:
                                response === true ? true && organizationsEnabled : false,
                            editableFields,
                            multipleValueFields,
                        });
                    }
                });
            });
        });
    }

    isFieldModified = (field) =>
        _.some(this.state.updatedFields || [], (item) => item.id === field.id);

    updateField = (field, updateFn) => {
        // The field being edited is always one of the editable ones.
        const fieldItem = _.find(this.getFields(), (item) => item.id === field.id)!;
        const newValues: string[] = [...(fieldItem.values || [])];
        updateFn(newValues);

        const updatedFields = (this.state.updatedFields || [])
            .filter((item) => item.id !== field.id)
            .concat(_.extend({}, fieldItem, { values: newValues }));
        const fieldsValid = this.validateFields(updatedFields);
        this.setState({ updatedFields, fieldsValid });
    };

    addField = (field) => {
        this.updateField(field, (values) => {
            if (values.length === 0) {
                // From the user"s perspective, zero values are displayed as one empty value. Thus we need to go from 0 -> 2.
                values.push("");
            }
            values.push("");
        });
    };

    removeField = (field, index) => {
        this.updateField(field, (values) => values.splice(index, 1));
    };

    setFieldValue = (field, event, newValue, valueIndex) => {
        this.updateField(field, (values) => {
            // eslint-disable-next-line no-param-reassign
            values[valueIndex] = newValue;
        });
    };

    save = () => {
        if (!this.state.fieldsValid) {
            Log.info("A mandatory field lacks value");
            return;
        }
        const updatedFields = this.state.updatedFields;
        let hasNewOrgs = !_.isEqual(
            (this.props.organizations || []).map((org) => org.id),
            this.state.selectedOrganizations.map((org) => org.id)
        );
        if (this.props.organizations === false) {
            hasNewOrgs = false;
        }
        if (this.state.canEditOrganizations === false) {
            hasNewOrgs = false;
        }
        if (!updatedFields && !hasNewOrgs) {
            this.props.onClose();
            return;
        }

        if (_.some(updatedFields ?? [], this.hasMultipleValues)) {
            // eslint-disable-next-line no-alert
            const confirmed = window.confirm(
                Language.get("nc_edit_reservation_fields_overwrite_warning")
            );
            if (!confirmed) {
                this.props.onClose();
                return;
            }
        }

        let errors: any[] = [];

        let apiCalls: ((done: () => void) => void)[] = [];
        if (updatedFields) {
            apiCalls = apiCalls.concat((done) => {
                API.setModifiableReservationFields(
                    this.props.reservationIds,
                    updatedFields,
                    (result) => {
                        const details = result
                            .filter((item) => item.details)
                            .map((item) => item.details);
                        errors = errors.concat(details);
                        done();
                    }
                );
            });
        }

        if (hasNewOrgs) {
            apiCalls = apiCalls.concat((done) => {
                API.setReservationOrganizations(
                    this.props.reservationIds,
                    this.state.selectedOrganizations.map((org) => org.id),
                    (result) => {
                        const details = result
                            .filter((item) => item.details)
                            .map((item) => item.details);
                        errors = errors.concat(details);
                        done();
                    }
                );
            });
        }

        _.runSync(apiCalls, () => {
            if (this.props.onUpdate) {
                this.props.onUpdate();
            }
            if (errors.length > 0) {
                Log.warning(errors[0]);
            } else {
                this.props.onClose();
            }
        });
    };

    hasMultipleValues = (field) => _.contains(this.state.multipleValueFields, field.id);

    bindSave = () => {
        this._previousSaveBinding = Mousetrap.unbindWithHelp("mod+s", true)[0];
        Mousetrap.bindWithHelp(
            "mod+s",
            () => {
                this.save();
                return false;
            },
            undefined,
            Language.get("dialog_save")
        );
    };

    getFields = () => {
        if (!this.state.updatedFields) {
            return this.state.editableFields;
        }

        return this.state.editableFields.map((field) => {
            const updatedField = _.find(
                this.state.updatedFields ?? [],
                (item) => item.id === field.id
            );
            return updatedField || field;
        });
    };

    hasMultipleReservations = () => this.props.reservationIds.length > 1;

    getFieldDef = (fieldId) =>
        _.find(this.props.fieldDefs, (field) => field.id === fieldId) || null;

    getDefinition(id, definitions) {
        return _.find(definitions, (def) => def.id === id) || null;
    }

    validateFields = (fields) =>
        _.every(
            fields,
            (field) => this.getFieldDef(field.id)?.mandatory !== true || this.isValidField(field)
        );

    isValidField = (field) =>
        field.values && field.values.length !== 0 && Boolean(field.values[0].trim());

    onOrganizationsChanged = (selectedOrganizations) => {
        this.setState({ selectedOrganizations });
    };

    onOrganizationSelected = (event) => {
        const definition = this.getDefinition(
            parseInt(event.target.value, 10),
            this.state.organizations
        );
        if (definition !== null) {
            const selectedOrganizations = this.state.selectedOrganizations.concat(definition);
            this.setState({ selectedOrganizations });
        }
    };

    _renderOrganizationSection() {
        return (
            <div className="accordionSection">
                <h3>{Language.get("dynamic_object_info_orgs")}</h3>
                <OrganizationList
                    items={this.state.selectedOrganizations}
                    editable={this.state.canEditOrganizations && this.props.organizations !== false}
                    propertyName={null}
                    dropdownOptions={{
                        options: this.state.organizations,
                        headlineValue: Language.get("nc_option_select"),
                    }}
                    label={Language.get("dynamic_object_info_orgs")}
                    onItemsChanged={this.onOrganizationsChanged}
                    onOptionSelected={this.onOrganizationSelected}
                    editFunction={_.noop}
                    searchObject={null}
                    updateSearchObject={_.noop}
                    totalNumber={this.state.organizations.length}
                />
            </div>
        );
    }

    render() {
        let title = Language.get("nc_cal_func_res_edit_fields");
        if (!this.hasMultipleReservations()) {
            title = `${title} (#${this.props.reservationIds[0]})`;
        } else {
            title = `${title} (${Language.get(
                "nc_mass_change_x_reservations",
                this.props.reservationIds.length
            )})`;
        }

        return (
            <div className="reservationInfo">
                <div className="accordion">
                    <div>
                        <div className="aSection first">
                            <h3>{title}</h3>
                            <div className="accordionSection">
                                <div>
                                    <table>
                                        {this.getFields().map((field, index) => {
                                            const definition = this.getFieldDef(field.id);
                                            if (!definition) {
                                                return null;
                                            }

                                            const indeterminate =
                                                this.hasMultipleValues(field) &&
                                                !this.isFieldModified(field);
                                            return (
                                                <FieldTableRow
                                                    key={field.id}
                                                    isFirstField={index === 0}
                                                    field={field}
                                                    definition={definition}
                                                    editable={
                                                        // Editable means different things on different field types.
                                                        // For category fields it means whether new categories can be added.
                                                        // For other fields, it means whether the user should be able to edit the field.
                                                        definition.kind ===
                                                            FieldInput.fieldKind.CATEGORY ||
                                                        definition.editable ||
                                                        false
                                                    }
                                                    indeterminate={indeterminate}
                                                    addField={this.addField.bind(this, field)}
                                                    removeField={this.removeField.bind(this, field)}
                                                    setFieldValue={this.setFieldValue.bind(
                                                        this,
                                                        field
                                                    )}
                                                />
                                            );
                                        }, this)}
                                    </table>
                                </div>
                            </div>
                            <div className="aSection last">{this._renderOrganizationSection()}</div>
                        </div>
                    </div>

                    {this.props.useNewUI ? (
                        <div className="btnGroup horizontal">
                            <Button onClick={this.props.onClose}>
                                {Language.get("dialog_cancel")}
                            </Button>
                            <Button onClick={this.save} disabled={!this.state.fieldsValid}>
                                {Language.get("dialog_save")}
                            </Button>
                        </div>
                    ) : (
                        <div className="btnGroup">
                            <button
                                className="deprecated save"
                                onClick={this.save}
                                disabled={!this.state.fieldsValid}
                            >
                                {Language.get("dialog_save")}
                            </button>
                            <button className="deprecated cancel" onClick={this.props.onClose}>
                                {Language.get("dialog_cancel")}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default ReservationFieldEditor;
